import { useEffect } from 'react';
import { liveNotificationChangedFrameSchema } from '@molt/shared';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { queryKeys } from './query-keys';

const BASE_RECONNECT_DELAY_MS = 500;
const MAX_RECONNECT_DELAY_MS = 10_000;
const CLOSE_POLICY_VIOLATION = 1008;
const CLOSE_NORMAL = 1000;

export interface NotificationSubscriptionRequest {
  readonly queryClient: QueryClient;
}

export interface NotificationSubscriptionHandle {
  readonly unsubscribe: () => void;
}

interface Subscriber {
  readonly request: NotificationSubscriptionRequest;
}

interface NotificationConnection {
  readonly subscribers: Set<Subscriber>;
  socket: WebSocket | null;
  attempt: number;
  reconnectTimer: ReturnType<typeof setTimeout> | null;
  disposed: boolean;
  refused: boolean;
}

export function notificationSocketUrl(): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/ws/notifications`;
}

function parseNotificationFrame(data: unknown): boolean {
  if (typeof data !== 'string') return false;
  let json: unknown;
  try {
    json = JSON.parse(data);
  } catch {
    return false;
  }
  return liveNotificationChangedFrameSchema.safeParse(json).success;
}

export class NotificationSocketClient {
  private connection: NotificationConnection | null = null;

  subscribe(request: NotificationSubscriptionRequest): NotificationSubscriptionHandle {
    const connection = this.connectionFor();
    const subscriber: Subscriber = { request };
    connection.subscribers.add(subscriber);

    let released = false;
    return {
      unsubscribe: () => {
        if (released) return;
        released = true;
        connection.subscribers.delete(subscriber);
        if (connection.subscribers.size === 0) this.dispose(connection);
      },
    };
  }

  private connectionFor(): NotificationConnection {
    if (this.connection !== null) {
      if (this.connection.refused) {
        this.connection.refused = false;
        this.connection.attempt = 0;
        this.open(this.connection);
      }
      return this.connection;
    }

    const connection: NotificationConnection = {
      subscribers: new Set(),
      socket: null,
      attempt: 0,
      reconnectTimer: null,
      disposed: false,
      refused: false,
    };
    this.connection = connection;
    this.open(connection);
    return connection;
  }

  private open(connection: NotificationConnection): void {
    const socket = new WebSocket(notificationSocketUrl());
    connection.socket = socket;

    socket.addEventListener('open', () => {
      if (connection.disposed) return;
      connection.attempt = 0;
    });

    socket.addEventListener('message', (event: MessageEvent) => {
      if (connection.disposed) return;
      if (!parseNotificationFrame(event.data)) return;
      for (const queryClient of this.clientsOf(connection)) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all() });
      }
    });

    socket.addEventListener('close', (event: CloseEvent) => {
      if (connection.disposed) return;
      if (connection.socket !== socket) return;
      connection.socket = null;

      if (event.code === CLOSE_POLICY_VIOLATION) {
        connection.refused = true;
        return;
      }

      this.scheduleReconnect(connection);
    });

    socket.addEventListener('error', () => {
      /* handled by the close listener that follows */
    });
  }

  private clientsOf(connection: NotificationConnection): Set<QueryClient> {
    const clients = new Set<QueryClient>();
    for (const subscriber of connection.subscribers) {
      clients.add(subscriber.request.queryClient);
    }
    return clients;
  }

  private scheduleReconnect(connection: NotificationConnection): void {
    const capped = Math.min(
      MAX_RECONNECT_DELAY_MS,
      BASE_RECONNECT_DELAY_MS * 2 ** connection.attempt,
    );
    const delay = capped / 2 + Math.random() * (capped / 2);
    connection.attempt += 1;

    connection.reconnectTimer = setTimeout(() => {
      connection.reconnectTimer = null;
      if (connection.disposed) return;
      if (connection.subscribers.size === 0) return;
      this.open(connection);
    }, delay);
  }

  private dispose(connection: NotificationConnection): void {
    connection.disposed = true;
    if (this.connection === connection) this.connection = null;

    if (connection.reconnectTimer !== null) {
      clearTimeout(connection.reconnectTimer);
      connection.reconnectTimer = null;
    }

    const socket = connection.socket;
    connection.socket = null;
    socket?.close(CLOSE_NORMAL);
  }
}

export const notificationSockets = new NotificationSocketClient();

export function useNotificationLive(enabled: boolean): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) return undefined;

    const subscription = notificationSockets.subscribe({ queryClient });
    return () => {
      subscription.unsubscribe();
    };
  }, [enabled, queryClient]);
}
